import { accountApi } from "../../config/apiConfig";
import {
  AuditListResponse,
  ContractDraftStatus,
  FirstChargeEmailResponse,
  GoLivePayload,
  GoLiveResponse,
  OnboardingState,
  ProspectDetail,
  ProspectListResponse,
  ProspectStationListResponse,
  ProspectUpdatePayload,
  Prospect,
  RevealStationKeyResponse,
  SaveStationCredentialsPayload,
  SetStatePayload,
  TechnicalNamePreview,
} from "../../types/onboarding";

// All admin prospect endpoints live on acc-management (accountApi auto-attaches
// the Bearer token via its request interceptor).

// GET /admin/prospects?state= — list (full scan, no server pagination in MVP).
export const listProspects = async (
  state?: OnboardingState | null
): Promise<ProspectListResponse> => {
  const query = state ? `?state=${encodeURIComponent(state)}` : "";
  const response = await accountApi.get(`admin/prospects${query}`);
  return response.data;
};

// GET /admin/prospects/{id} — full detail incl. transitions.
export const getProspect = async (id: string): Promise<ProspectDetail> => {
  const response = await accountApi.get(`admin/prospects/${id}`);
  return response.data;
};

// GET /admin/prospects/{id}/audit — per-field audit history (most-recent-first).
export const getProspectAudit = async (
  id: string
): Promise<AuditListResponse> => {
  const response = await accountApi.get(`admin/prospects/${id}/audit`);
  return response.data;
};

// POST /admin/prospects/{id}/state — advance state / more-info / resign.
export const setProspectState = async (
  id: string,
  payload: SetStatePayload
): Promise<Prospect> => {
  const response = await accountApi.post(`admin/prospects/${id}/state`, payload);
  return response.data;
};

// PUT /admin/prospects/{id} — ops edit (partial update; only changed fields).
export const updateProspect = async (
  id: string,
  payload: ProspectUpdatePayload
): Promise<Prospect> => {
  const response = await accountApi.put(`admin/prospects/${id}`, payload);
  return response.data;
};

// GET /admin/technical-names/next?count= — preview next auto-generated names.
export const previewTechnicalNames = async (
  count: number
): Promise<TechnicalNamePreview> => {
  const response = await accountApi.get(
    `admin/technical-names/next?count=${count}`
  );
  return response.data;
};

// PATCH /admin/prospects/{id}/technical-name — assign (auto when custom_names omitted).
export const assignTechnicalNames = async (
  id: string,
  customNames?: string[]
): Promise<Prospect> => {
  const body = customNames ? { custom_names: customNames } : {};
  const response = await accountApi.patch(
    `admin/prospects/${id}/technical-name`,
    body
  );
  return response.data;
};

// POST /admin/prospects/{id}/contract-draft — request (or re-request) the
// contract draft. No request body. The draft is generated in the background
// (CWA-187): the answer is 202 with a PENDING status (200 with the finished
// status only where the backend generates inline, i.e. locally). Once it
// succeeds the backend advances the state to CONTRACT_DRAFT_CREATED and
// persists the link.
export const generateContractDraft = async (
  id: string
): Promise<ContractDraftStatus> => {
  const response = await accountApi.post(
    `admin/prospects/${id}/contract-draft`
  );
  return response.data;
};

// GET /admin/prospects/{id}/contract-draft/status — the latest draft request:
// NONE, PENDING, SUCCEEDED (with the new link) or FAILED (with an error code).
export const getContractDraftStatus = async (
  id: string
): Promise<ContractDraftStatus> => {
  const response = await accountApi.get(
    `admin/prospects/${id}/contract-draft/status`
  );
  return response.data;
};

// ---- Station setup & configuration delivery (CWA-91) ----
//
// The three action endpoints below take no request body, but only
// register-stations declares @Consumes(WILDCARD) on the backend — promote and
// send-config-email inherit application/json. Posting with no data at all sends
// no Content-Type and risks a 415, so every one of them sends an explicit {}.

// GET /admin/prospects/{id}/stations — one row per assigned station identifier,
// with the status of its OCPP access data. Carries no access key — reading one is
// a separate, audited call (revealStationKey).
export const listProspectStations = async (
  id: string
): Promise<ProspectStationListResponse> => {
  const response = await accountApi.get(`admin/prospects/${id}/stations`);
  return response.data;
};

// POST /admin/prospects/{id}/stations/{stationId}/credentials — correct one
// station's access data. Leaving authorization_key out makes the backend
// generate a fresh key, so ops can rotate without handling the secret. 204.
export const saveStationCredentials = async (
  id: string,
  stationId: string,
  payload: SaveStationCredentialsPayload
): Promise<void> => {
  await accountApi.post(
    `admin/prospects/${id}/stations/${encodeURIComponent(stationId)}/credentials`,
    payload
  );
};

// POST /admin/prospects/{id}/confirm-publication — the same operation as go-live
// under the name that matches what an agent is doing once a station has reported
// itself available. The backend delegates to the same handler, so the body and
// the response are identical (contract §3).
export const confirmProspectPublication = async (
  id: string,
  stationIds?: string[]
): Promise<GoLiveResponse> => {
  const response = await accountApi.post(
    `admin/prospects/${id}/confirm-publication`,
    stationIds?.length ? { station_ids: stationIds } : {}
  );
  return response.data;
};

// POST /admin/prospects/{id}/stations/{stationId}/reveal-key — the station's
// OCPP access key, for entering into eRound by hand. Audited on every call, so
// it is only ever requested for the one station an agent asked for.
export const revealStationKey = async (
  id: string,
  stationId: string
): Promise<RevealStationKeyResponse> => {
  const response = await accountApi.post(
    `admin/prospects/${id}/stations/${encodeURIComponent(stationId)}/reveal-key`,
    {}
  );
  return response.data;
};

// POST /admin/prospects/{id}/register-stations — records that every station has
// access data (→ OCPP_CREDENTIALS_GENERATED). 204; 400 lists the stations that
// are still missing access data.
export const registerProspectStations = async (id: string): Promise<void> => {
  await accountApi.post(`admin/prospects/${id}/register-stations`, {});
};

// POST /admin/prospects/{id}/promote — → CUSTOMER_PROMOTED. Returns the updated
// prospect. Normally already done automatically after signing; this is the
// recovery path. The clearing/permission side effects are best-effort on the
// backend and are NOT reported here, so callers must not claim they succeeded.
export const promoteProspect = async (id: string): Promise<Prospect> => {
  const response = await accountApi.post(`admin/prospects/${id}/promote`, {});
  return response.data;
};

// POST /admin/prospects/{id}/send-config-email — sends (or re-sends) the
// configuration email. 204. First send advances to OCPP_CREDENTIALS_SENT; a
// resend from that state deliberately leaves the state unchanged.
export const sendConfigEmail = async (id: string): Promise<void> => {
  await accountApi.post(`admin/prospects/${id}/send-config-email`, {});
};

// POST /admin/prospects/{id}/go-live — marks the prospect's stations public and
// sends the congratulations email. From OCPP_CREDENTIALS_SENT, WELCOME_BOX_SENT
// or STATION_AVAILABLE it advances to STATION_PUBLIC; from STATION_PUBLIC it
// only re-sends the email.
//
// stationIds decides which stations the CUSTOMER'S EMAIL names — nothing more.
// The backend derives every station's live status from the prospect's state, so
// a subset does not leave the rest un-live (CWA-94, research R3).
//
// The response carries the email outcome separately: a failed email still
// answers 200, because the state change is never rolled back.
export const markProspectLive = async (
  id: string,
  stationIds?: string[]
): Promise<GoLiveResponse> => {
  const payload: GoLivePayload = stationIds?.length
    ? { station_ids: stationIds }
    : {};
  // Always send a body — the endpoint declares no wildcard consumer, and
  // bodyless POSTs to its siblings answered 415.
  const response = await accountApi.post(
    `admin/prospects/${id}/go-live`,
    payload
  );
  return response.data;
};

// POST /admin/prospects/{id}/first-charge-email — re-sends the welcome e-mail a
// customer gets after their station's first charging session. No request body,
// no state change, and no duplicate guard: every call sends another e-mail.
//
// The response is camelCase, alone in this API — the backend returns a bare map
// instead of a snake_case response object. It is passed through untouched.
export const sendFirstChargeEmail = async (
  id: string
): Promise<FirstChargeEmailResponse> => {
  // Always send a body — the endpoint declares no wildcard consumer, and
  // bodyless POSTs to its siblings answered 415.
  const response = await accountApi.post(
    `admin/prospects/${id}/first-charge-email`,
    {}
  );
  return response.data;
};

